import { FullStoryAPI } from 'react-fullstory';
import { LoadMyBookings, LoadMyRecentTrips } from "../Booking/BookingLoaders";
import { Config } from "../../Config/Config";
import { LoadMyCards, GetUserMpsProfile, LoadEmptyListOfCards, SetDeviceData } from "../Payment/PaymentHelper";
import { PromoteGuestBookings } from "../MyBookings/Logic/PromoteGuestBookings";
import { GetMyTemplates } from '../BookingTemplate/BookingTemplateHelper';
import { FeatureFlags } from '../../Config/FeatureFlags';
import { ValidateProfile } from './ProfileValidation';
import { Dispatch } from '../Dispatch';
import { TryLoadAndPersistProfile, TryRestoreUserProfile } from './GetUserProfileService';
import { ShowDialogRetryErrorMessage, ShowDialogSimpleErrorMessage } from '../../widgets/general-error-message/ErrorMessagingHelper';
import { TryAgainMessageKind, WellKnownMessageKind } from '../Utils/ErrorMessages';
import { TryRenewAuth0Session } from './AuthHelper';
import { SimpleUserProfile } from '../User/ProfileEntitiesV2';
import appstore from '../../appStore';
import { LogEvent } from '../../utils/LogEvent';
import { AuthToken, LoginStatusKind } from './AuthEntities';
import { DeriveUserIdFromAuthToken } from './ProcessUserProfile';
import { LoadMyGbBookings } from '../GbBooking/GbBookingLoaders';
import { PayDriverOption } from '../Payment/PaymentEntities';
import { ConvertToPaymentOption } from '../Payment/PaymentHandler';
import { LoadMyAddressPoints } from '../AddressPoints/LoadAddressPoints';
import { ConsiderShowingNewFeaturePopup } from '../Booking/Mobile/ConsiderShowingNewFeaturePopup';
import { LogEventInGA4 } from '../../utils/LogEventsInGoogleAnalytics';
import { MyStorage } from '../../Storage';
import { CredentialPopupTrigger } from '../UILogicControl/UILogicControlEntities';
import { appInsights } from "../../client";

/** 
 * User has just logged in or signed up.
 * Load their profile and other user data, then do validation.
 */
export async function StartLoginSession(isNewSignUp: boolean) {

    Dispatch.Payment.ClearCardError();

    // Do not show error any existing message
    Dispatch.UILogicControl.OnIsStrictValidationModeOnBookingFormChange(false);

    const profile = await TryLoadAndPersistProfile(true);
    if (!profile) {

        ShowDialogRetryErrorMessage(TryAgainMessageKind.Auth0LoginTryAgain);
        return;
    }

    // Log a GA4 event when User is Logged In
    LogEventInGA4.UserLogin(profile.UserId, LoginStatusKind.LoggedIn);

    AttachUserIdToTracing(profile);

    await PromoteGuestBookings();
    await LoadAllMyUserData(isNewSignUp);
    await ValidateProfile(isNewSignUp);
    ShowAddPaymentCardScreenAfterSignup();
    ConsiderTurningOffFixedPrice();
}

/** 
 *  Attempt to restore our entire login session. 
 *  The Auth0 session must be validated before we can make any API calls.
 *  This is called at application startup (i.e. fresh page visit or browser refresh).
 */
export async function TryRestoreLoginSession(originalToken: AuthToken) {

    LogEvent.HttpReferrer(document.referrer);

    // if this fails, we are logged out
    const newToken = await TryRenewAuth0Session();
    if (!newToken) return;

    // check for persistence mismatch
    const oldUserId = DeriveUserIdFromAuthToken(originalToken);
    const newUserId = DeriveUserIdFromAuthToken(newToken);

    let profile: SimpleUserProfile | null = null;

    if (oldUserId !== newUserId) {
        LogEvent.LoginRestoreUserChanged(oldUserId, newUserId);

        // data is uncertain; reload from server, logout if failed
        profile = await TryLoadAndPersistProfile(true);
    }
    else {
        // first try to load profile from API
        profile = await TryLoadAndPersistProfile(false);

        // loading from the API failed, restore from local storage. it might not be the latest data in the profile. but it will be updated with the next page refresh (rare situation)
        if (!profile) profile = await TryRestoreUserProfile(newToken);
    }

    await CompleteLoginRestore(profile);
}

/** 
 *  Attempt to restore login session from SSO.
 */
export async function TryRestoreLoginSessionFromSingleSignOn() {

    LogEvent.HttpReferrer(document.referrer);

    // if this fails, we are logged out
    const token = await TryRenewAuth0Session();
    if (!token) return;

    // load profile from server
    let profile: SimpleUserProfile | null = await TryLoadAndPersistProfile(true);

    await CompleteLoginRestore(profile);
}

/** 
 *  Restore related information per profile, and do profile validation.
 */
async function CompleteLoginRestore(profile: SimpleUserProfile | null) {

    // abort on failure
    if (!profile) {
        ShowDialogSimpleErrorMessage(WellKnownMessageKind.Auth0RenewFailure);
        return;
    }

    AttachUserIdToTracing(profile);

    await LoadAllMyUserData(false);
    await ValidateProfile(false);
}

/**
 * Apply the logged in Profile's User ID to our tracing tech.
 */
function AttachUserIdToTracing(userProfile: SimpleUserProfile) {

    // Adding user identity data to fullstory tracking for signed in users.
    FullStoryAPI('identify', userProfile.UserId, { displayName: userProfile.DisplayName });

    // Adding user ID to Appinsights tracking
    appInsights.setAuthenticatedUserContext(userProfile.UserId.toString());
}

/** 
 *  Various data loads for a signed in user.
 *  There is some benefit in not awaiting these API calls: to allow them to run in parallel.
 *  However, any calls that create a User record on first usage will be awaited to avoid race conditions. These are all very fast anyway (~25ms).
 */
async function LoadAllMyUserData(isNewSignUp: boolean) {

    GetUserMpsProfile(isNewSignUp);
    LoadPaymentCards(isNewSignUp);

    SetDeviceData();
    if (!isNewSignUp) {
        LoadMyRecentTrips();
        await LoadMyAddressPoints();
    }

    // Get the list of booking templates (favourites)
    if (FeatureFlags.BookingTemplates && !isNewSignUp) GetMyTemplates();    

    // This needs to be awaited. The result resets the MyBookings.All redux store slice. It can remove tracking link bookings if not awaited.
    if (!isNewSignUp) {
        if (FeatureFlags.GlobalBooker) {
            await LoadMyGbBookings(Config.StatusListInScheduleViewFromV2API);
        } else {
            await LoadMyBookings(Config.StatusListInScheduleView);            
        }
    }

    // when latest feature is new to user, show latest feature card to logged in user.
    ConsiderShowingNewFeaturePopup(isNewSignUp);
}

export async function LoadPaymentCards(isNewSignUp: boolean) {

    if (isNewSignUp) {
        LoadEmptyListOfCards();
    }
    else {
        await LoadMyCards();
    }

    SelectDefaultPaymentOption();
}

/** Select the user's default payment option in the booking widget. */
function SelectDefaultPaymentOption() {

    let payment = PayDriverOption;
    const defaultCard = appstore.getState().payment.DefaultCard;

    if (defaultCard) {
        payment = ConvertToPaymentOption(defaultCard);
    }

    Dispatch.Booking.PaymentMethod(payment);

    // If there is a default payment method stored in localstorage (this payment method is not an item in the user's cards list), select that as the default option in the payment dropdown overriding the default card in the cards list.
    const defaultPaymentMethod = MyStorage.DefaultPaymentMethod.LoadData();

    if (defaultPaymentMethod) {
        Dispatch.Booking.PaymentMethod(defaultPaymentMethod);
    }
}

/**
 * Show the add payment card screen to the newly registered user, who has clicked the "Add a new card" option from the payment method 
 */
function ShowAddPaymentCardScreenAfterSignup() {

    const isUserAddingPaymentCard = appstore.getState().payment.ShouldDisplayAddPaymentCardScreenAfterSignup;

    if (!FeatureFlags.CardNotPresentPayment || !isUserAddingPaymentCard) return;

    LogEvent.ShowAddCardScreenAfterSignup();

    Dispatch.Payment.ToggleCardRegistrationPanel(true);
    Dispatch.Payment.HideAddPaymentCardScreenAfterSignup();
}

/** Consider to turn off Fixed Price automatically on login. This will turn off the toggle switch in the booking form and update some other UI to match the state. */
function ConsiderTurningOffFixedPrice() {
    const signUpTrigger = appstore.getState().uiLogicControl.Credentials.PopupTriggerSource;

    // in this scenario, the user has already decided to pay the driver directly, so we turn off Fixed Price to avoid confusion to the user.
    if(signUpTrigger === CredentialPopupTrigger.FromPayDriverDirectLink) {
        Dispatch.Condition.ClearPriceGuarantee();
    };
}